import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { RHFCheckboxField } from '@openx/components-core/lib/CheckboxField/RHFCheckboxField';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections';

import { ACL_TYPE } from '@openx/types/aclType';
import { TargetingFields, type TargetingFieldsValues } from '@openx/types/targeting/targeting';
import { noop } from '@openx/utils/noop';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { type ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChangesFromInfoTag } from '../ChangesFromInfoTag';

const StyledIndentedSections = styled(IndentedSections)`
  margin-top: 0;
`;

const StyledGridSwitch = styled(Grid, {
  shouldForwardProp: prop => prop !== 'showCheckbox',
})<{ showCheckbox: boolean }>`
  padding-left: ${({ theme }) => theme.spacing(0.5)};
  margin-bottom: ${({ showCheckbox, theme }) => (showCheckbox ? 0 : theme.spacing(1.5))};
`;

const StyledGridCheckbox = styled(Grid)`
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  padding-left: ${({ theme }) => theme.spacing(0.5)};
`;

type ExcludeIndirectirectProps = {
  excludeIndirectValue: boolean;
  includeOpenBiddingInventory: boolean;
  onFieldUpdate?: (field: TargetingFieldsValues, newData: boolean) => void;
  readonly?: boolean;
};

export function ExcludeIndirect({
  excludeIndirectValue,
  includeOpenBiddingInventory,
  onFieldUpdate = noop,
  readonly = false,
}: ExcludeIndirectirectProps) {
  const [updatedByUserManually, setUpdatedByUserManually] = useState(false);
  const { isOa, useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();

  const { t } = useTranslation();

  const isAllowedToSee = isOa || isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_READ));
  const isAllowedToEdit = isOa || isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE));

  const toggleExcludeIndirect = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setUpdatedByUserManually(true);
      onFieldUpdate(TargetingFields.EXCLUDE_INDIRECT, checked);

      if (!checked) {
        onFieldUpdate(TargetingFields.INCLUDE_OPEN_BIDDING_INVENTORY, false);
      }
    },
    [onFieldUpdate]
  );

  if (!isAllowedToSee) {
    return null;
  }

  if (readonly) {
    if (!excludeIndirectValue) {
      return null;
    }
    return (
      <>
        <StyledGridSwitch showCheckbox={excludeIndirectValue} size={12} sx={{ pl: 0 }}>
          <FieldDetails title={t('Exclude Indirect Inventory')} checkbox value={excludeIndirectValue} />
        </StyledGridSwitch>

        {!!includeOpenBiddingInventory && (
          <Grid size={12}>
            <StyledIndentedSections>
              <FieldDetails
                title={t('Include Google Open Bidding Inventory')}
                checkbox
                value={includeOpenBiddingInventory}
              />
            </StyledIndentedSections>
          </Grid>
        )}
      </>
    );
  }

  return (
    <>
      <StyledGridSwitch showCheckbox={excludeIndirectValue} size={12}>
        <RHFCheckboxField
          name={`targeting.${TargetingFields.EXCLUDE_INDIRECT}`}
          label={t('Exclude Indirect Inventory')}
          data-test="exclude-indirect-inventory"
          disabled={!isAllowedToEdit}
          onChange={toggleExcludeIndirect}
        />

        {!updatedByUserManually && <ChangesFromInfoTag fieldName={TargetingFields.EXCLUDE_INDIRECT} />}
      </StyledGridSwitch>

      {excludeIndirectValue && (
        <StyledGridCheckbox sx={{ display: 'block' }} size={12}>
          <StyledIndentedSections>
            <RHFCheckboxField
              name={`targeting.${TargetingFields.INCLUDE_OPEN_BIDDING_INVENTORY}`}
              label={t('Include Google Open Bidding Inventory')}
              data-test="include-google-open-bidding-inventory"
              disabled={!isAllowedToEdit}
              onChange={(_event, checked) => {
                onFieldUpdate(TargetingFields.INCLUDE_OPEN_BIDDING_INVENTORY, checked);
              }}
            />
          </StyledIndentedSections>
        </StyledGridCheckbox>
      )}
    </>
  );
}
